<div class="tl-card flex max-h-full w-full flex-col overflow-hidden font-sans shadow-md">
  <!-- Photo header. Present only when the tank has photos: with none, the title
       row below takes over as the header and carries the status chip inline. -->
  <div class="tl-map-card-photo relative flex-shrink-0" *ngIf="hasPhotos">
    <app-photo-carousel
      class="block"
      [photos]="photos"
      [showPrimaryBadge]="false"
      navPlacement="overlay"
      (photoClick)="photoClick.emit($event)"
    ></app-photo-carousel>

    <div
      class="absolute left-3 top-3 z-10 flex size-12 items-center justify-center rounded-full bg-white shadow-md"
    >
      <ng-container *ngTemplateOutlet="levelChart"></ng-container>
    </div>

    <button
      type="button"
      class="absolute right-2 top-2 z-10 flex size-8 cursor-pointer items-center justify-center rounded-full border-none bg-white/90 p-0 leading-none text-theme-black"
      (click)="cardCloseClick.emit()"
      aria-label="Close"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <div class="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-6">
    <!-- Title row -->
    <div class="flex flex-row items-start gap-3">
      <div
        class="tl-map-card-inline-head flex-shrink-0"
        [class.tl-map-card-inline-head--deferred]="hasPhotos"
      >
        <ng-container *ngTemplateOutlet="levelChart"></ng-container>
      </div>

      <a
        class="min-w-0 flex-1 cursor-pointer text-base font-bold text-theme underline"
        [routerLink]="['/', 'tank', tankInfo?.uniqueID, 'details']"
      >
        {{ tankInfo?.tankName }}
      </a>

      <div class="flex flex-shrink-0 flex-col items-end text-right">
        <span class="text-base font-bold" [ngClass]="statusTextClass">
          {{ volumePct }}% {{ 'TankMap.Full' | translate }}
        </span>
        <span class="text-xs text-gray-500">
          ({{ tankInfo?.volume | number: '1.2-2' }} {{ tankInfo?.uomSuffix }})
        </span>
      </div>

      <button
        type="button"
        class="tl-map-card-inline-head -mr-1 -mt-1 flex size-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-transparent p-0 leading-none text-theme-black"
        [class.tl-map-card-inline-head--deferred]="hasPhotos"
        (click)="cardCloseClick.emit()"
        aria-label="Close"
      >
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <!-- Detail rows -->
    <dl class="m-0 flex flex-col gap-1.5 text-sm">
      <div class="flex items-baseline justify-between gap-4">
        <dt class="font-normal text-gray-500">{{ 'TankMap.LocationName' | translate }}</dt>
        <dd class="m-0 text-right font-semibold text-theme-black">{{ tankInfo?.locationName }}</dd>
      </div>
      <div class="flex items-baseline justify-between gap-4">
        <dt class="font-normal text-gray-500">{{ 'TankMap.RTUNumber' | translate }}</dt>
        <dd class="m-0 text-right font-semibold text-theme-black">{{ tankInfo?.rtuNumber }}</dd>
      </div>
      <div class="flex items-baseline justify-between gap-4">
        <dt class="font-normal text-gray-500">{{ 'TankMap.Latitude' | translate }}</dt>
        <dd class="m-0 text-right font-semibold text-theme-black">{{ tankInfo?.latitude }}</dd>
      </div>
      <div class="flex items-baseline justify-between gap-4">
        <dt class="font-normal text-gray-500">{{ 'TankMap.Longitude' | translate }}</dt>
        <dd class="m-0 text-right font-semibold text-theme-black">{{ tankInfo?.longitude }}</dd>
      </div>
      <div class="flex items-baseline justify-between gap-4">
        <dt class="font-normal text-gray-500">{{ 'TankMap.Address' | translate }}</dt>
        <dd class="m-0 text-right font-semibold text-theme-black">{{ tankInfo?.formattedAddress }}</dd>
      </div>
      <div class="flex items-baseline justify-between gap-4">
        <dt class="font-normal text-gray-500">{{ 'TankMap.Product' | translate }}</dt>
        <dd class="m-0 text-right font-semibold text-theme-black">{{ tankInfo?.product }}</dd>
      </div>
      <div class="flex items-baseline justify-between gap-4">
        <dt class="font-normal text-gray-500">{{ 'TankMap.DaysToOrder' | translate }}</dt>
        <dd class="m-0 text-right font-semibold text-theme">{{ tankInfo?.daysToOrder | number: '1.2-2' }}</dd>
      </div>
      <div class="flex items-baseline justify-between gap-4">
        <dt class="font-normal text-gray-500">{{ 'TankMap.DaysToEmpty' | translate }}</dt>
        <dd class="m-0 text-right font-semibold text-theme-black">{{ tankInfo?.daysToEmpty | number: '1.2-2' }}</dd>
      </div>
    </dl>

    <!-- Full-bleed band: the chart itself carries no card chrome, the section
         around it is shaded and ruled off from the detail rows above. -->
    <div
      class="-mx-6 -mb-6 mt-1 border-x-0 border-y border-solid border-pill bg-surface px-6 py-2"
    >
      <app-tank-daily-usage-chart
        chartTitle="TankMap.Label.DailyUsage"
        [tankId]="tankInfo?.tankID"
      ></app-tank-daily-usage-chart>
    </div>
  </div>

  <!-- Tank carousel: several tanks share this marker's coordinates. -->
  <div
    class="flex flex-shrink-0 flex-row items-center bg-white px-2 py-2 text-sm"
    *ngIf="carouselCount > 1"
  >
    <button
      type="button"
      class="flex cursor-pointer select-none items-center border-none bg-transparent p-0 text-theme"
      (click)="prevCarouselMarker.emit()"
    >
      <mat-icon>chevron_left</mat-icon>
      {{ 'TankMap.PrevTank' | translate }}
    </button>
    <span class="flex-1 select-none text-center text-gray-500">
      {{ carouselIndex + 1 }} {{ 'TankMap.Of' | translate }} {{ carouselCount }}
    </span>
    <button
      type="button"
      class="flex cursor-pointer select-none items-center border-none bg-transparent p-0 text-theme"
      (click)="nextCarouselMarker.emit()"
    >
      {{ 'TankMap.NextTank' | translate }}
      <mat-icon>chevron_right</mat-icon>
    </button>
  </div>
</div>

<ng-template #levelChart>
  <app-tank-volume-level-chart
    [fillColor]="fillColor"
    [tankLevelPercent]="volumePct / 100"
    height="34px"
    [showVolumeLevelLabels]="false"
  ></app-tank-volume-level-chart>
</ng-template>
